function stripContraPrefix(description, isContra) {
	if (!isContra) return description;
	return description
		.replace(/^accumulated\s+(depreciation|amortization)\s*[-–—]?\s*/i, "")
		.replace(/^accum\.?\s*(deprec|amort)\.?\s*[-–—]?\s*/i, "")
		.trim();
}

function classKey(className) {
	return className.toLowerCase().replace(/[^a-z0-9]/g, "");
}

const CurrentYearMovementsService = {
	/**
	 * Computes current-year balances by applying classified transactions and adjusting entries
	 * to the prior-year closing balances. Only runs on CaseWare-imported statements (detected by
	 * a `schedule` field); balance-sheet accounts carry prior forward, income-statement accounts
	 * reset to the year's movement. Returns enriched balanceSheet, incomeStatement, retainedEarnings
	 * and ppeNote.
	 * @param {Object} params { balanceSheet, incomeStatement, transactions, adjustingEntries, retainedEarnings, ppeNote }
	 * @return {Object} { balanceSheet, incomeStatement, retainedEarnings, ppeNote }
	 */
	applyCurrentYearMovements({ balanceSheet, incomeStatement, transactions, adjustingEntries, retainedEarnings, ppeNote }) {
		if (!balanceSheet || !incomeStatement) return { balanceSheet: balanceSheet || { sections: [] }, incomeStatement: incomeStatement || { sections: [] }, retainedEarnings: retainedEarnings || null, ppeNote: ppeNote || null };
		if (!balanceSheet.sections || !incomeStatement.sections) return { balanceSheet, incomeStatement, retainedEarnings: retainedEarnings || null, ppeNote: ppeNote || null };

		let hasScheduleField = false;
		for (let section of (balanceSheet.sections || [])) {
			for (let account of (section.accounts || [])) {
				if (account.schedule) { hasScheduleField = true; break; }
			}
			if (hasScheduleField) break;
		}
		if (!hasScheduleField) return { balanceSheet, incomeStatement, retainedEarnings: retainedEarnings || null, ppeNote: ppeNote || null };

		let hasTx = Array.isArray(transactions) && transactions.length > 0;
		let hasAje = Array.isArray(adjustingEntries) && adjustingEntries.length > 0;
		if (!hasTx && !hasAje) return { balanceSheet, incomeStatement, retainedEarnings: retainedEarnings || null, ppeNote: ppeNote || null };

		let rawMovements = this.aggregateRawMovements({ transactions, adjustingEntries });
		let isSections = this.applySectionMovements(incomeStatement.sections, rawMovements, false);
		let netIncomeCurrent = this.computeNetIncome(isSections);
		let bsSections = this.applySectionMovements(balanceSheet.sections, rawMovements, true);
		bsSections = this.injectNetIncome(bsSections, netIncomeCurrent);
		bsSections = this.reclassifyNegativeLiabilitiesToAssets(bsSections);

		let enrichedBalanceSheet = this.rebuildBalanceSheetTotals(balanceSheet, bsSections);

		return {
			balanceSheet: enrichedBalanceSheet,
			incomeStatement: this.rebuildIncomeStatementTotals(incomeStatement, isSections, netIncomeCurrent),
			retainedEarnings: this.rollForwardRetainedEarnings(retainedEarnings, netIncomeCurrent),
			ppeNote: this.rollForwardPPENote(ppeNote, enrichedBalanceSheet),
		};
	},

	aggregateRawMovements({ transactions, adjustingEntries }) {
		let rawMovements = {};

		for (let tx of (transactions || [])) {
			if (!tx.classification || tx.classification === "Unclassified") continue;
			let amount = Number(tx.amount) || 0;
			if (amount === 0) continue;

			let bankAcct = tx.bankAccountNumber;
			if (bankAcct) {
				rawMovements[bankAcct] = (rawMovements[bankAcct] || 0) + amount;
			}

			let classMatch = tx.classification.match(/^(\d+)/);
			if (classMatch) {
				let classAcct = classMatch[1];
				rawMovements[classAcct] = (rawMovements[classAcct] || 0) + (-amount);
			}
		}

		for (let aje of (adjustingEntries || [])) {
			for (let line of (aje.lines || [])) {
				let amount = Math.abs(Number(line.amount) || 0);
				if (amount === 0) continue;
				if (!line.account) continue;
				let signed = line.side === "DEBIT" ? amount : -amount;
				rawMovements[line.account] = (rawMovements[line.account] || 0) + signed;
			}
		}

		return rawMovements;
	},

	applySectionMovements(sections, rawMovements, carryForward) {
		return sections.map(function (section) {
			let accounts = section.accounts.map(function (acct) {
				let raw = rawMovements[acct.accountNumber] || 0;
				let displayMovement = raw * (acct.sign || 1);
				let current = carryForward ? (acct.prior || 0) + displayMovement : displayMovement;
				return { ...acct, current };
			});
			return { ...section, accounts, totalCurrent: accounts.reduce(function (sum, a) { return sum + a.current; }, 0) };
		});
	},

	computeNetIncome(isSections) {
		return isSections.reduce(function (sum, s) {
			let isAdditive = s.category === "revenue" || s.category === "other-income";
			return sum + (isAdditive ? s.totalCurrent : -s.totalCurrent);
		}, 0);
	},

	injectNetIncome(bsSections, netIncomeCurrent) {
		return bsSections.map(function (section) {
			if (section.category !== "equity") return section;
			let accounts = section.accounts.map(function (acct) {
				if (acct.accountNumber === "NETINC") return { ...acct, current: netIncomeCurrent };
				return acct;
			});
			return { ...section, accounts, totalCurrent: accounts.reduce(function (sum, a) { return sum + a.current; }, 0) };
		});
	},

	reclassifyNegativeLiabilitiesToAssets(sections) {
		let LIABILITY_CATS = new Set(["current-liabilities", "long-term-liabilities"]);
		let accountsToMove = [];

		let updatedSections = sections.map(function (section) {
			if (!LIABILITY_CATS.has(section.category)) return section;

			let keepAccounts = [];
			for (let acct of section.accounts) {
				if (acct.reclassifiable && acct.current < 0) {
					accountsToMove.push({ ...acct, current: -acct.current });
				} else {
					keepAccounts.push(acct);
				}
			}

			if (keepAccounts.length === section.accounts.length) return section;
			return { ...section, accounts: keepAccounts, totalCurrent: keepAccounts.reduce(function (sum, a) { return sum + a.current; }, 0) };
		});

		if (accountsToMove.length === 0) return updatedSections;

		let otherAssetsIndex = -1;
		for (let sectionIndex = 0; sectionIndex < updatedSections.length; sectionIndex++) {
			if (updatedSections[sectionIndex].name === "Other Assets" || updatedSections[sectionIndex].category === "other-assets") {
				otherAssetsIndex = sectionIndex;
				break;
			}
		}

		if (otherAssetsIndex >= 0) {
			let section = updatedSections[otherAssetsIndex];
			let newAccounts = section.accounts.concat(accountsToMove);
			updatedSections[otherAssetsIndex] = { ...section, accounts: newAccounts, totalCurrent: newAccounts.reduce(function (sum, a) { return sum + a.current; }, 0) };
		} else {
			updatedSections.push({
				name: "Other Assets",
				category: "other-assets",
				lineno: 8999,
				accounts: accountsToMove,
				totalCurrent: accountsToMove.reduce(function (sum, a) { return sum + a.current; }, 0),
				totalPrior: 0,
				totalPriorPrior: 0,
			});
		}

		return updatedSections;
	},

	rebuildBalanceSheetTotals(original, sections) {
		let isAsset = function (s) { return s.category === "current-assets" || s.category === "capital-assets" || s.category === "other-assets"; };
		let isLiab = function (s) { return s.category === "current-liabilities" || s.category === "long-term-liabilities"; };
		let isEquity = function (s) { return s.category === "equity"; };
		let sumCurrent = function (secs, fn) { return secs.filter(fn).reduce(function (sum, s) { return sum + s.totalCurrent; }, 0); };

		let totalAssetsCurrent = sumCurrent(sections, isAsset);
		let totalLiabCurrent = sumCurrent(sections, isLiab);
		let totalEquityCurrent = sumCurrent(sections, isEquity);

		return {
			...original,
			sections,
			totalAssets: { ...original.totalAssets, current: totalAssetsCurrent },
			totalLiabilities: { ...original.totalLiabilities, current: totalLiabCurrent },
			totalEquity: { ...original.totalEquity, current: totalEquityCurrent },
			totalLiabilitiesAndEquity: { ...original.totalLiabilitiesAndEquity, current: totalLiabCurrent + totalEquityCurrent },
		};
	},

	rebuildIncomeStatementTotals(original, sections, netIncomeCurrent) {
		let sumCurrent = function (secs, fn) { return secs.filter(fn).reduce(function (sum, s) { return sum + s.totalCurrent; }, 0); };

		return {
			...original,
			sections,
			totalRevenue: { ...original.totalRevenue, current: sumCurrent(sections, function (s) { return s.category === "revenue"; }) },
			totalExpenses: { ...original.totalExpenses, current: sumCurrent(sections, function (s) { return s.category === "expenses" || s.category === "cogs"; }) },
			netIncome: { ...original.netIncome, current: netIncomeCurrent },
		};
	},

	/**
	 * Restates retained earnings for the year's movements, keeping everything it does not recompute.
	 *
	 * The original is spread first because this rebuilds the object, and anything the server said
	 * about the statement that is not one of the four figures below would otherwise be dropped on
	 * this path alone — including whether the opening balance was actually found. A caller reading
	 * that flag would see it on an unenriched statement and lose it on an enriched one.
	 *
	 * @param {Object} retainedEarnings - as the server built it
	 * @param {Number} netIncomeCurrent
	 * @return {Object|null}
	 */
	rollForwardRetainedEarnings(retainedEarnings, netIncomeCurrent) {
		if (!retainedEarnings) return null;
		let openingCurrent = (retainedEarnings.closingBalance && retainedEarnings.closingBalance.prior) || 0;
		let movements = retainedEarnings.movements || [];
		let movementsCurrent = movements.reduce(function (runningTotal, movement) { return runningTotal + (movement.current || 0); }, 0);
		let closingCurrent = openingCurrent + (netIncomeCurrent || 0) + movementsCurrent;
		return {
			...retainedEarnings,
			openingBalance: { ...retainedEarnings.openingBalance, current: openingCurrent },
			netIncome: { ...retainedEarnings.netIncome, current: netIncomeCurrent || 0 },
			movements: movements,
			closingBalance: { ...retainedEarnings.closingBalance, current: closingCurrent },
		};
	},

	/**
	 * Restates the capital assets note for the year's movements, in the shape the compilation sends.
	 *
	 * The note lists each class at cost and holds accumulated depreciation once, against the total,
	 * ending at net book value — that is the presentation, and it is also the only shape the server
	 * produces. This used to expect a per-class accumulated amortisation and net book value that has
	 * never existed, so it read an absent field and threw, taking the statements down with it on
	 * every CaseWare import.
	 *
	 * Classes are matched to balance-sheet accounts by their description, because a CaseWare account
	 * carries no canonical code to join on. Contra accounts are recognised by their sign and summed
	 * into the single accumulated figure, which stays negative so net book value remains cost plus
	 * accumulated depreciation rather than a subtraction the caller has to remember.
	 *
	 * @param {Object} ppeNote - as afsService.buildCapitalAssetsNote builds it
	 * @param {Object} balanceSheet - the balance sheet already restated for the year
	 * @return {Object|null}
	 */
	rollForwardPPENote(ppeNote, balanceSheet) {
		if (!ppeNote || !ppeNote.classes || ppeNote.classes.length === 0) return ppeNote;

		let costCurrentByClass = new Map();
		let accumulatedDepreciationCurrent = 0;
		for (let section of (balanceSheet.sections || [])) {
			if (section.category !== "capital-assets") continue;
			for (let account of (section.accounts || [])) {
				let isContra = (account.sign || 1) === -1;
				let displayed = Math.abs(account.current || 0);
				if (isContra) {
					accumulatedDepreciationCurrent += displayed;
					continue;
				}
				let key = classKey(stripContraPrefix(account.description || "", false));
				costCurrentByClass.set(key, (costCurrentByClass.get(key) || 0) + displayed);
			}
		}

		let classes = ppeNote.classes.map(function (assetClass) {
			let key = classKey(assetClass.description || "");
			let costCurrent = costCurrentByClass.has(key) ? costCurrentByClass.get(key) : (assetClass.cost.prior || 0);
			return { ...assetClass, cost: { ...assetClass.cost, current: costCurrent } };
		});

		let totalCostCurrent = classes.reduce(function (runningTotal, assetClass) { return runningTotal + assetClass.cost.current; }, 0);
		let accumulatedCurrent = -accumulatedDepreciationCurrent;

		return {
			...ppeNote,
			classes: classes,
			totalCost: { ...ppeNote.totalCost, current: totalCostCurrent },
			accumulatedDepreciation: { ...ppeNote.accumulatedDepreciation, current: accumulatedCurrent },
			netBookValue: { ...ppeNote.netBookValue, current: totalCostCurrent + accumulatedCurrent },
		};
	},
};

export default CurrentYearMovementsService;
